/**
 * Workshop Active Learning Path — topics grouped by day with date/time.
 * Subtopic durations roll up to topic totals (same as live mandatory/optional).
 */

import { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { GripVertical, Trash2 } from 'lucide-react';
import type { Topic, TopicSubtopic } from '../../types/curriculumTopic';
import { formatTotalDuration, sumPlannedMinutes } from '../../utils/durationFormat';
import { reorderList } from '../../utils/reorder';
import { ROUTES } from '../../utils/createRoutes';
import { TopicDurationField } from '../shared/TopicDurationField';
import { DateTimeField } from '../schedule/DateTimeField';
import {
  allTopicsHaveDuration,
  saveCurriculumPathForTimeline,
} from '../../utils/curriculumPathStorage';
import {
  addMinutesToDateTime,
  mergeDayTimings,
  type WorkshopDayTiming,
} from '../../utils/workshopSessionUtils';

interface WorkshopDayLearningPathProps {
  topics: Topic[];
  dayCount: number;
  daySchedules: WorkshopDayTiming[];
  workshopStartDate: string;
  workshopStartTime: string;
  onRemove: (id: string) => void;
  onReorder: (topics: Topic[]) => void;
  onDurationChange: (id: string, durationMinutes: number) => void;
  onChangeTopicDay: (id: string, dayIndex: number) => void;
  onDayScheduleChange: (
    dayIndex: number,
    patch: { startDate?: string; startTime?: string },
  ) => void;
  onSubtopicDurationChange?: (
    topicId: string,
    subId: string,
    minutes: number,
  ) => void;
  onSubtopicRemove?: (topicId: string, subId: string) => void;
  onSubtopicTitleChange?: (topicId: string, subId: string, title: string) => void;
  onSubtopicReorder?: (topicId: string, subtopics: TopicSubtopic[]) => void;
  onAddDayTargetChange?: (dayIndex: number) => void;
  addDayTarget: number;
  courseId?: string;
  onContinue?: () => void;
  /** Course plan seed: no add/remove/reorder; durations + day date/time only. */
  structureLocked?: boolean;
}

export function WorkshopDayLearningPath({
  topics,
  dayCount,
  daySchedules,
  workshopStartDate,
  workshopStartTime,
  onRemove,
  onReorder,
  onDurationChange,
  onChangeTopicDay,
  onDayScheduleChange,
  onSubtopicDurationChange,
  onSubtopicRemove,
  onSubtopicTitleChange,
  onSubtopicReorder,
  onAddDayTargetChange,
  addDayTarget,
  courseId,
  onContinue,
  structureLocked = false,
}: WorkshopDayLearningPathProps) {
  const navigate = useNavigate();
  const [dragOrder, setDragOrder] = useState<{
    dayIndex: number;
    drag: number | null;
    over: number | null;
  }>({ dayIndex: 0, drag: null, over: null });

  const [subDrag, setSubDrag] = useState<{
    topicId: string;
    idx: number;
  } | null>(null);
  const [subOver, setSubOver] = useState<{
    topicId: string;
    idx: number;
  } | null>(null);

  const timings = useMemo(
    () =>
      mergeDayTimings(
        dayCount,
        workshopStartDate,
        workshopStartTime,
        daySchedules,
      ),
    [dayCount, workshopStartDate, workshopStartTime, daySchedules],
  );

  const topicsByDay = useMemo(() => {
    const days: Topic[][] = Array.from({ length: dayCount }, () => []);
    for (const topic of topics) {
      const dayIndex = Math.min(
        Math.max(topic.workshopDayIndex ?? 0, 0),
        Math.max(dayCount - 1, 0),
      );
      days[dayIndex].push(topic);
    }
    return days;
  }, [topics, dayCount]);

  const canAddToTimeline = allTopicsHaveDuration(topics);

  const clearDrag = () =>
    setDragOrder({ dayIndex: 0, drag: null, over: null });

  const clearSubDrag = () => {
    setSubDrag(null);
    setSubOver(null);
  };

  const handleDrop = (dayIndex: number, toLocalIndex: number) => {
    if (
      dragOrder.drag === null ||
      dragOrder.dayIndex !== dayIndex ||
      dragOrder.drag === toLocalIndex
    ) {
      clearDrag();
      return;
    }
    const dayTopics = topicsByDay[dayIndex] || [];
    const reorderedDay = reorderList(dayTopics, dragOrder.drag, toLocalIndex);
    const next = topicsByDay.flatMap((day, idx) =>
      idx === dayIndex ? reorderedDay : day,
    );
    onReorder(next);
    clearDrag();
  };

  const handleSubDrop = useCallback(
    (topicId: string, toIdx: number) => {
      if (!subDrag || subDrag.topicId !== topicId || subDrag.idx === toIdx) {
        clearSubDrag();
        return;
      }
      const topic = topics.find((t) => t.id === topicId);
      if (!topic?.subtopics) {
        clearSubDrag();
        return;
      }
      const selected = topic.subtopics.filter((s) => s.selected);
      const reordered = reorderList(selected, subDrag.idx, toIdx);
      const unselected = topic.subtopics.filter((s) => !s.selected);
      onSubtopicReorder?.(topicId, [...reordered, ...unselected]);
      clearSubDrag();
    },
    [subDrag, topics, onSubtopicReorder],
  );

  const handleContinue = () => {
    if (!canAddToTimeline) return;
    if (onContinue) {
      onContinue();
      return;
    }
    saveCurriculumPathForTimeline(topics);
    navigate(ROUTES.topicPlanner, { state: { fromCurriculum: true } });
  };

  return (
    <div className="codelive-active-path">
      <div className="card">
        <div className="card-header">
          <h6 className="mb-0">Day-wise Learning Path</h6>
          <small className="text-muted">
            {structureLocked
              ? 'Set date/time per day and subtopic durations — topics are fixed from the course plan'
              : 'Set date/time per day · set subtopic durations · drag to reorder'}
          </small>
          <div className="d-flex justify-content-between small mt-2">
            <span>Total: {formatTotalDuration(sumPlannedMinutes(topics))}</span>
            <span>
              {topics.length} Modules · {dayCount} Day{dayCount === 1 ? '' : 's'}
            </span>
          </div>
          {onAddDayTargetChange && !structureLocked ? (
            <div className="mt-2">
              <label className="form-label small mb-1">Add topics to</label>
              <select
                className="form-select form-select-sm"
                value={addDayTarget}
                onChange={(e) => onAddDayTargetChange(Number(e.target.value))}
              >
                {Array.from({ length: dayCount }, (_, i) => (
                  <option key={i} value={i}>
                    Day {i + 1}
                  </option>
                ))}
              </select>
            </div>
          ) : null}
        </div>
        <div className="card-body d-flex flex-column gap-3">
          {timings.map((timing) => {
            const dayTopics = topicsByDay[timing.dayIndex] || [];
            const dayMinutes = sumPlannedMinutes(dayTopics);
            const ends =
              dayMinutes > 0
                ? addMinutesToDateTime(
                    timing.startDate,
                    timing.startTime,
                    dayMinutes,
                  )
                : null;
            return (
              <div
                key={timing.dayIndex}
                className="rounded border border-warning-subtle bg-light p-2"
              >
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <p className="mb-0 fw-semibold small">
                    Day {timing.dayIndex + 1}
                    {ends ? ` · ends ${ends.endTime}` : ''}
                  </p>
                  <span className="small text-muted">
                    {dayTopics.length} topic{dayTopics.length === 1 ? '' : 's'} ·{' '}
                    {formatTotalDuration(dayMinutes)}
                  </span>
                </div>
                <DateTimeField
                  label="Date / start time"
                  date={timing.startDate}
                  time={timing.startTime}
                  onDateChange={(value) =>
                    onDayScheduleChange(timing.dayIndex, { startDate: value })
                  }
                  onTimeChange={(value) =>
                    onDayScheduleChange(timing.dayIndex, { startTime: value })
                  }
                />
                <div className="d-flex flex-column gap-2 mt-2">
                  {dayTopics.length === 0 ? (
                    <p className="small text-muted mb-0 px-1">
                      No topics for this day yet.
                    </p>
                  ) : (
                    dayTopics.map((topic, localIndex) => {
                      const isDragging =
                        dragOrder.dayIndex === timing.dayIndex &&
                        dragOrder.drag === localIndex;
                      const isOver =
                        dragOrder.dayIndex === timing.dayIndex &&
                        dragOrder.over === localIndex &&
                        dragOrder.drag !== localIndex;
                      const selectedSubs =
                        topic.subtopics?.filter((s) => s.selected) ?? [];
                      const topicTotal =
                        selectedSubs.length > 0
                          ? selectedSubs.reduce(
                              (sum, s) => sum + s.durationMinutes,
                              0,
                            )
                          : topic.durationMinutes || 0;

                      return (
                        <div
                          key={topic.id}
                          className={`border rounded bg-white ${
                            isDragging ? 'opacity-50' : ''
                          } ${isOver ? 'border-primary' : ''}`}
                        >
                          <div
                            draggable={!structureLocked}
                            onDragStart={(e) => {
                              if (structureLocked) return;
                              e.stopPropagation();
                              setDragOrder({
                                dayIndex: timing.dayIndex,
                                drag: localIndex,
                                over: null,
                              });
                            }}
                            onDragEnd={clearDrag}
                            onDragOver={(e) => {
                              if (structureLocked) return;
                              e.preventDefault();
                              e.dataTransfer.dropEffect = 'move';
                              setDragOrder((prev) => ({
                                ...prev,
                                dayIndex: timing.dayIndex,
                                over: localIndex,
                              }));
                            }}
                            onDrop={(e) => {
                              if (structureLocked) return;
                              e.preventDefault();
                              handleDrop(timing.dayIndex, localIndex);
                            }}
                            className="d-flex align-items-start gap-2 p-2"
                            style={{
                              cursor: structureLocked ? 'default' : 'grab',
                              borderBottom:
                                selectedSubs.length > 0
                                  ? '1px solid #e2e8f0'
                                  : 'none',
                            }}
                          >
                            {!structureLocked && (
                            <GripVertical
                              className="text-muted flex-shrink-0 mt-1"
                              size={16}
                            />
                            )}
                            <div className="flex-grow-1 min-w-0">
                              <p className="mb-0 small fw-semibold text-truncate">
                                {topic.title}
                              </p>
                              <div
                                className="d-flex flex-wrap align-items-center gap-2 mt-1"
                                onMouseDown={(e) => e.stopPropagation()}
                              >
                                {!structureLocked && (
                                  <>
                                <label className="small text-muted mb-0">
                                  Day
                                </label>
                                <select
                                  className="form-select form-select-sm"
                                  style={{ width: 'auto', minWidth: 88 }}
                                  value={
                                    topic.workshopDayIndex ?? timing.dayIndex
                                  }
                                  draggable={false}
                                  onClick={(e) => e.stopPropagation()}
                                  onChange={(e) =>
                                    onChangeTopicDay(
                                      topic.id,
                                      Number(e.target.value),
                                    )
                                  }
                                  aria-label={`Assign ${topic.title} to a workshop day`}
                                >
                                  {Array.from({ length: dayCount }, (_, i) => (
                                    <option key={i} value={i}>
                                      Day {i + 1}
                                    </option>
                                  ))}
                                </select>
                                  </>
                                )}
                                {structureLocked && (
                                  <span className="badge text-bg-light border small">
                                    Day {(topic.workshopDayIndex ?? timing.dayIndex) + 1}
                                  </span>
                                )}
                                {selectedSubs.length === 0 ? (
                                  <TopicDurationField
                                    topicId={topic.id}
                                    topicTitle={topic.title}
                                    minutes={topic.durationMinutes}
                                    courseId={courseId}
                                    onChange={(m) =>
                                      onDurationChange(topic.id, m)
                                    }
                                    onMouseDownStop={(e) => e.stopPropagation()}
                                  />
                                ) : topicTotal > 0 ? (
                                  <span
                                    className="small text-muted fw-semibold"
                                    title="Auto-calculated from subtopics"
                                  >
                                    {topicTotal} min
                                  </span>
                                ) : null}
                              </div>
                            </div>
                            {!structureLocked && (
                            <button
                              type="button"
                              draggable={false}
                              onClick={() => onRemove(topic.id)}
                              className="btn btn-sm btn-outline-danger flex-shrink-0"
                              aria-label={`Remove ${topic.title}`}
                            >
                              <Trash2 size={14} />
                            </button>
                            )}
                          </div>

                          {selectedSubs.length > 0 ? (
                            <div className="px-2 pb-2 d-flex flex-column gap-2">
                              {selectedSubs.map((sub, subIdx) => {
                                const isSubDragging =
                                  subDrag?.topicId === topic.id &&
                                  subDrag.idx === subIdx;
                                const isSubOver =
                                  subOver?.topicId === topic.id &&
                                  subOver.idx === subIdx &&
                                  subDrag?.topicId === topic.id &&
                                  subDrag.idx !== subIdx;

                                return (
                                  <div
                                    key={sub.id}
                                    draggable={!structureLocked}
                                    onDragStart={(e) => {
                                      if (structureLocked) return;
                                      e.stopPropagation();
                                      setSubDrag({
                                        topicId: topic.id,
                                        idx: subIdx,
                                      });
                                      setSubOver(null);
                                    }}
                                    onDragEnd={clearSubDrag}
                                    onDragOver={(e) => {
                                      if (structureLocked) return;
                                      e.preventDefault();
                                      e.stopPropagation();
                                      e.dataTransfer.dropEffect = 'move';
                                      setSubOver({
                                        topicId: topic.id,
                                        idx: subIdx,
                                      });
                                    }}
                                    onDrop={(e) => {
                                      if (structureLocked) return;
                                      e.preventDefault();
                                      e.stopPropagation();
                                      handleSubDrop(topic.id, subIdx);
                                    }}
                                    className={`border px-2 py-2 ${
                                      isSubDragging ? 'opacity-50' : ''
                                    }`}
                                    style={{
                                      marginLeft: 8,
                                      borderRadius: 8,
                                      background: '#f9fafb',
                                      borderColor: isSubOver
                                        ? '#2563eb'
                                        : '#e2e8f0',
                                      borderWidth: isSubOver ? 2 : 1,
                                      cursor: structureLocked ? 'default' : 'grab',
                                    }}
                                  >
                                    <div className="d-flex align-items-center gap-2">
                                      {!structureLocked && (
                                      <GripVertical
                                        className="text-muted flex-shrink-0"
                                        size={12}
                                      />
                                      )}
                                      <span className="small fw-medium flex-grow-1 text-truncate">
                                        {sub.title || 'Subtopic'}
                                      </span>
                                      {!structureLocked && (
                                      <button
                                        type="button"
                                        draggable={false}
                                        onClick={() =>
                                          onSubtopicRemove?.(topic.id, sub.id)
                                        }
                                        className="btn btn-sm p-0 text-danger flex-shrink-0"
                                        aria-label={`Remove ${sub.title}`}
                                      >
                                        <Trash2 size={13} />
                                      </button>
                                      )}
                                    </div>
                                    <div
                                      className="mt-1"
                                      style={{ paddingLeft: 20 }}
                                      onMouseDown={(e) => e.stopPropagation()}
                                    >
                                      <TopicDurationField
                                        topicId={sub.id}
                                        topicTitle={sub.title}
                                        minutes={
                                          sub.durationMinutes > 0
                                            ? sub.durationMinutes
                                            : null
                                        }
                                        courseId={courseId}
                                        onChange={(m) =>
                                          onSubtopicDurationChange?.(
                                            topic.id,
                                            sub.id,
                                            m,
                                          )
                                        }
                                        onMouseDownStop={(e) =>
                                          e.stopPropagation()
                                        }
                                      />
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          ) : null}
                        </div>
                      );
                    })
                  )}
                </div>
              </div>
            );
          })}
        </div>
        <div className="card-footer">
          <button
            type="button"
            className={`btn w-100 ${canAddToTimeline ? 'btn-primary' : 'btn-secondary'}`}
            style={{ opacity: canAddToTimeline ? 1 : 0.65 }}
            disabled={!canAddToTimeline}
            onClick={handleContinue}
          >
            {structureLocked ? 'Continue' : 'Add to Timeline'}
          </button>
          {!canAddToTimeline ? (
            <p className="small text-warning mb-0 mt-2 text-center">
              {structureLocked
                ? 'Set a duration for every subtopic before continuing.'
                : 'Add at least one subtopic to every topic and set a duration for each before adding to the timeline.'}
            </p>
          ) : null}
        </div>
      </div>
    </div>
  );
}
